import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import NumberInput from 'components/inputs/number';
import Text from 'components/inputs/text';
import { ScoringScenarioAdminType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { errorModal, successModal } from 'utils/ui';

const MULTIPLIER_FIELDS: Array<{
  key: keyof Omit<ScoringScenarioAdminType, 'id' | 'code' | 'label'>;
  label: string;
}> = [
  { key: 'comfortPerformanceMultiplier', label: 'Comfort & Performance' },
  { key: 'installationDifficultyMultiplier', label: 'Install Difficulty' },
  { key: 'noiseMultiplier', label: 'Noise' },
  { key: 'valueMultiplier', label: 'Value' }
];

export default function ScoringScenariosPage() {
  const dispatch = useAppDispatch();
  const { scoringScenarios } = useAppSelector((state) => state.admin);
  const [localScenarios, setLocalScenarios] = useState<
    ScoringScenarioAdminType[]
  >([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLocalScenarios(scoringScenarios);
  }, [scoringScenarios]);

  const filteredScenarios = useMemo(() => {
    if (!search) return localScenarios;
    const lower = search.toLowerCase();
    return localScenarios.filter(
      (s) =>
        s.code.toLowerCase().includes(lower) ||
        s.label.toLowerCase().includes(lower)
    );
  }, [localScenarios, search]);

  const hasChanges =
    JSON.stringify(localScenarios) !== JSON.stringify(scoringScenarios);

  const updateField = <K extends keyof ScoringScenarioAdminType>(
    id: number,
    key: K,
    value: ScoringScenarioAdminType[K]
  ) => {
    setLocalScenarios((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [key]: value } : s))
    );
  };

  const handleSave = async () => {
    try {
      const changed = localScenarios.filter((s) => {
        const original = scoringScenarios.find((o) => o.id === s.id);
        if (!original) return false;
        return (
          original.label !== s.label ||
          original.comfortPerformanceMultiplier !==
            s.comfortPerformanceMultiplier ||
          original.installationDifficultyMultiplier !==
            s.installationDifficultyMultiplier ||
          original.noiseMultiplier !== s.noiseMultiplier ||
          original.valueMultiplier !== s.valueMultiplier
        );
      });
      if (changed.length === 0) {
        successModal('No changes to save.');
        return;
      }
      await axios.patch(
        'admin/scoring-scenarios',
        changed.map((s) => ({
          id: s.id,
          label: s.label,
          comfortPerformanceMultiplier: s.comfortPerformanceMultiplier,
          installationDifficultyMultiplier: s.installationDifficultyMultiplier,
          noiseMultiplier: s.noiseMultiplier,
          valueMultiplier: s.valueMultiplier
        }))
      );
      dispatch(fetchDefaultAdminData());
      successModal('Scoring scenarios updated successfully.');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    setLocalScenarios(scoringScenarios);
  };

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>Scoring Scenarios</div>
      <div className='text-sm text-gray-600'>
        Preset scenarios that scale how the four scoring categories contribute
        to a system&rsquo;s total. Code is locked because the scoring engine
        references scenarios by their stable code.
      </div>
      <div className='flex items-center gap-3'>
        <Button
          text='Save'
          color='green'
          onClick={handleSave}
          disabled={!hasChanges}
        />
        <Button
          text='Reset'
          color='red'
          onClick={handleReset}
          disabled={!hasChanges}
        />
        <div className='ml-auto w-64'>
          <Text
            value={search}
            onChange={setSearch}
            placeholder='Search code or label...'
            controlled
          />
        </div>
      </div>

      <div className='flex flex-col gap-3'>
        {filteredScenarios.map((scenario) => (
          <div
            key={scenario.id}
            className='flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm'
          >
            <div className='flex items-center gap-4'>
              <span className='shrink-0 rounded bg-gray-100 px-2 py-1 font-mono text-sm text-gray-700'>
                {scenario.code}
              </span>
              <div className='min-w-0 flex-1'>
                <Text
                  value={scenario.label}
                  onChange={(value) => updateField(scenario.id, 'label', value)}
                  controlled
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6'>
              {MULTIPLIER_FIELDS.map((field) => (
                <div key={field.key} className='flex flex-col gap-1'>
                  <label className='text-xs font-semibold text-gray-700'>
                    {field.label}
                  </label>
                  <NumberInput
                    value={scenario[field.key]}
                    onChange={(value) =>
                      updateField(scenario.id, field.key, value ?? 0)
                    }
                    step={0.01}
                    min={0}
                    size='sm'
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        {filteredScenarios.length === 0 && (
          <div className='py-8 text-center text-gray-500'>
            No scenarios match your search.
          </div>
        )}
      </div>
      <div className='text-sm text-gray-400'>
        {filteredScenarios.length} scenario
        {filteredScenarios.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
